if (!customElements.get('td-back-in-stock')) {
  const SETTLE_DELAY = 50;

  const SELECTORS = {
    root: '[id^="ProductInfo-"]',
    notify: '[data-td-bis-notify]',
    submit: '[data-td-bis-submit]',
    variantData: 'variant-radios script[type="application/json"], variant-selects script[type="application/json"]',
    picker: 'variant-radios, variant-selects',
    variantInput: 'product-form input[name="id"]',
  };

  // Submit handler for the Klaviyo back-in-stock form. Bound once globally; the
  // form/company ids come from the clicked <td-back-in-stock> element (stashed
  // on window at click time) so they live in markup, not in this shared asset.
  if (!window.__tdBisKlaviyoBound) {
    window.__tdBisKlaviyoBound = true;
    window.addEventListener('klaviyoForms', (e) => {
      if (
        !window._bisVariantId ||
        !window._bisCompanyId ||
        e.detail.formId !== window._bisFormId ||
        e.detail.type !== 'stepSubmit' ||
        !e.detail.metaData ||
        !e.detail.metaData.$email
      ) return;

      if (window._bisEmail === e.detail.metaData.$email) return;
      window._bisEmail = e.detail.metaData.$email;

      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${window._bisCompanyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: e.detail.metaData.$email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$custom:::$default:::${window._bisVariantId}` } },
            },
          },
        }),
      })
        .then((response) => {
          if (response.status === 202) {
            window._bisVariantId = null;
            window._bisEmail = null;
          } else {
            // Clear the email guard so the shopper can retry the same address.
            window._bisEmail = null;
            response.json().then((data) => console.error('BIS subscription error', data));
          }
        })
        .catch((error) => {
          window._bisEmail = null;
          console.error('BIS subscription failed', error);
        });
    });
  }

  class TdBackInStock extends HTMLElement {
    connectedCallback() {
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onNotifyClick = this.onNotifyClick.bind(this);

      // Size/dropdown changes flow through variant-radios -> tdVariantChange.
      // Combined-listing colour swaps replace the section markup, which re-runs
      // this callback on the fresh element — no extra hook needed.
      document.addEventListener('tdVariantChange', this.onVariantChange);
      // `click` is synthesised reliably on tap; a separate touchend listener
      // would fire onNotifyClick twice and reopen the form mid-flow.
      this.addEventListener('click', this.onNotifyClick);

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('tdVariantChange', this.onVariantChange);
      clearTimeout(this.settleTimer);
    }

    get root() {
      return this.closest(SELECTORS.root) || document;
    }

    getVariants() {
      const script = this.root.querySelector(SELECTORS.variantData);
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return [];
      }
    }

    // Resolve the fully-selected variant from the settled DOM. The event payload
    // alone is unreliable — swatch selection updates the checked input
    // asynchronously, so the first change can still report the previous variant.
    resolveVariant() {
      const variants = this.getVariants();
      if (!variants.length) return null;

      const picker = this.root.querySelector(SELECTORS.picker);
      if (picker) {
        const values = [];
        const fieldsets = picker.querySelectorAll('fieldset');
        if (fieldsets.length) {
          fieldsets.forEach((fs) => {
            const checked = fs.querySelector('input:checked');
            if (checked) values.push(checked.value);
          });
        } else {
          picker.querySelectorAll('select').forEach((sel) => values.push(sel.value));
        }
        if (values.length) {
          const match = variants.find(
            (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])
          );
          if (match) return match;
        }
      }

      const input = this.root.querySelector(SELECTORS.variantInput);
      if (input && input.value) {
        return variants.find((v) => v.id == input.value) || null;
      }
      return null;
    }

    // Show the waitlist button (and hide add-to-cart) for unavailable variants.
    apply(variant) {
      const notify = this.querySelector(SELECTORS.notify);
      const submit = this.root.querySelector(SELECTORS.submit);
      if (!notify) return;

      if (variant && variant.available === false) {
        notify.style.display = 'block';
        notify.setAttribute('data-variant-id', variant.id);
        if (submit) submit.style.display = 'none';
      } else {
        notify.style.display = 'none';
        notify.removeAttribute('data-variant-id');
        if (submit) submit.style.display = '';
      }
    }

    // Apply immediately, then again once the selection has settled (swatch
    // selection updates the checked input asynchronously).
    sync(variant) {
      this.apply(variant || this.resolveVariant());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.apply(this.resolveVariant()), SETTLE_DELAY);
    }

    onVariantChange(e) {
      this.sync(e && e.detail && e.detail.variant);
    }

    // Stash the variant id (plus the Klaviyo ids from this element) and open the
    // back-in-stock form. Ids come from data attributes so they can be changed
    // in Liquid/settings without editing this asset.
    onNotifyClick(e) {
      const button = e.target.closest(SELECTORS.notify);
      if (!button) return;
      const variantId = button.getAttribute('data-variant-id');
      const formId = this.dataset.formId;
      const companyId = this.dataset.companyId;
      if (!variantId || !formId || !companyId) return;

      window._bisVariantId = variantId;
      window._bisFormId = formId;
      window._bisCompanyId = companyId;
      window._bisEmail = null;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    }
  }

  customElements.define('td-back-in-stock', TdBackInStock);
}
